import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import LogoutButton from "./LogoutButton";
import {
  BookmarkSimple,
  HighlighterCircle,
  List,
  Moon,
  NotePencil,
  Sun,
  UserCircle,
  XCircle,
} from "@phosphor-icons/react";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
// import { Button } from "./ui/button";
import bgImage from "../assets/header-img.webp";
import { RootState } from "@/redux/store";
import LogoImage from "@/assets/Logo Ion.png";
import DarkLogoImage from "@/assets/Dark Logo Ion.png";
import AccountAuthModal from "./AccountAuthModal";

const Header: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return localStorage.getItem("ezra-theme") === "dark";
  });

  {
    /* State to control account modal  */
  }
  const [showAccountModal, setShowAccountModal] = useState<boolean>(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  {
    /* State to control mobile menu bar  */
  }
  const [showMenu, setShowMenu] = useState<boolean>(false);

  {
    /* State and Effect to control header by listening the scroll */
  }
  const [show, handleShow] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDarkMode);
    localStorage.setItem("ezra-theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        handleShow(true);
      } else handleShow(false);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  {
    /* Function to open the account modal */
  }
  const handleAccountClick = () => {
    setShowAccountModal((prev) => !prev);
  };

  {
    /* Function to open the mobile menu */
  }
  const handleMenuClick = () => {
    setShowMenu((prev) => !prev);
  };

  {
    /* Function to close the mobile menu */
  }
  const closeMenu = () => {
    setShowMenu(false);
  };

  {
    /* Ref to listen the curser and close the account modal */
  }
  const ref = useRef<HTMLLIElement>(null);
  useOnClickOutside(ref, showAccountModal, () => setShowAccountModal(false));

  const ThemeToggle = ({ compact = false }: { compact?: boolean }) => (
    <button
      type="button"
      onClick={() => setIsDarkMode((current) => !current)}
      className={`flex items-center justify-center rounded-full border transition-all ${
        compact
          ? "h-8 w-8 border-accent-6/40 bg-white/90 text-secondary-8 hover:border-accent-6"
          : "h-9 w-9 border-white/30 bg-white/10 text-white backdrop-blur hover:bg-white/20"
      } dark:border-accent-6/40 dark:bg-secondary-9 dark:text-accent-3 dark:hover:bg-secondary-8`}
      aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
      title={isDarkMode ? "Light mode" : "Dark mode"}
    >
      {isDarkMode ? <Sun size={18} weight="bold" /> : <Moon size={18} weight="bold" />}
    </button>
  );

  const closeAccountActions = () => {
    setShowAccountModal(false);
    setShowAuthModal(false);
    closeMenu();
  };

  const AccountDropdown = () => (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="absolute right-0 top-full z-[160] mt-2 w-64 overflow-hidden rounded-2xl border border-accent-6/35 bg-white/95 p-2 text-secondary-8 shadow-[0_18px_45px_rgba(0,0,0,0.22)] backdrop-blur-xl dark:bg-secondary-9/95 dark:text-primary-1"
      data-testid="account-modal"
    >
      <span className="absolute -top-2 right-8 h-4 w-4 rotate-45 border-l border-t border-accent-6/35 bg-white/95 dark:bg-secondary-9/95" />
      <div className="relative border-b border-accent-6/20 px-3 py-3">
        <p className="text-sm text-secondary-5 dark:text-primary-6">Signed in as</p>
        <p className="truncate text-base text-secondary-8 dark:text-primary-1">
          {user?.firstName} {user?.lastName}
        </p>
      </div>
      <NavLink
        to="/profile"
        onClick={closeAccountActions}
        className="relative mt-2 block rounded-xl px-3 py-3 text-sm transition-colors hover:bg-accent-6 hover:text-white"
      >
        Profile & Settings
      </NavLink>
      {user?.role === "Admin" && (
        <NavLink
          to="/admin"
          onClick={closeAccountActions}
          className="relative block rounded-xl px-3 py-3 text-sm transition-colors hover:bg-accent-6 hover:text-white"
        >
          Dashboard
        </NavLink>
      )}
      <div className="relative mt-1 border-y border-accent-6/20 py-2">
        <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-secondary-4 dark:text-primary-6">
          Bible study
        </p>
        <NavLink
          to="/bible/library/notes"
          onClick={closeAccountActions}
          className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-accent-6 hover:text-white"
        >
          <NotePencil size={17} /> Notes
        </NavLink>
        <NavLink
          to="/bible/library/bookmarks"
          onClick={closeAccountActions}
          className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-accent-6 hover:text-white"
        >
          <BookmarkSimple size={17} /> Saved chapters
        </NavLink>
        <NavLink
          to="/bible/library/highlights"
          onClick={closeAccountActions}
          className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-accent-6 hover:text-white"
        >
          <HighlighterCircle size={17} /> Highlighted verses
        </NavLink>
      </div>
      <div className="relative mt-1 rounded-xl px-3 py-3 text-sm transition-colors hover:bg-red-500/10 dark:hover:bg-red-500/20">
        <LogoutButton />
      </div>
    </motion.div>
  );

  return (
    <header className="relative z-[100] mb-8 max-w-screen font-nokia-bold">
      {/* Header Background Image */}
      <div className="absolute top-0 z-0 w-full h-16  lg:h-[4.2rem] xl:h-[4.8rem] shadow-xl">
        <img
          src={bgImage}
          className="w-full h-full  object-cover"
          alt="Background"
        />
      </div>
      {/* Header when the scroll is above 20  */}
      <div className="relative z-50 py-2">
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 1 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.1 }}
            className="fixed top-0  z-0 w-full h-16 lg:h-[4.5rem] xl:h-[5.5rem]   bg-white shadow-xl"
          >
            {/* Header Background Image */}
            {/* <img
              src={bgImage}
              className="w-full h-full -mt-3 object-cover"
              alt="Background"
            /> */}
            {/* Navigation and Logo */}
            <div className="absolute top-[34%]  w-full md:top-[28%] lg:top-[26.3%]">
              <div className="  flex justify-between items-center  w-[90%] md:w-[90%] lg:w-[85%] mx-auto ">
                <div className="flex justify-center items-center gap-2 md:space-x-0   xl:space-x-1 cursor-pointer ">
                  <img
                    src={DarkLogoImage}
                    className="w-4  md:w-5  lg:w-6  xl:w-7 "
                    alt=""
                  />
                  <NavLink to="/" onClick={closeMenu}>
                    <h3 className="text-xs md:text-sm lg:text-lg xl:text-2xl tracking-wide font-nokia-bold text-secondary-6">
                      <strong>Ezra</strong> Seminary
                    </h3>
                  </NavLink>
                </div>
                {/* MenuBar */}
                <div className="md:hidden flex">
                  <button
                    onClick={handleMenuClick}
                    className=" focus:outline-none "
                  >
                    {showMenu ? (
                      <XCircle
                        size={20}
                        className="z-20 fixed top-[1.4rem] left-[90%] block text-primary-1"
                      />
                    ) : (
                      <List
                        size={20}
                        className="text-secondary-6 fixed top-[1.4rem] left-[90%] block"
                      />
                    )}
                  </button>
                </div>
                {/* Navigation */}
                <ul
                  className={`${
                    showMenu
                      ? "flex flex-col justify-center items-end text-xl text-primary-1 font-nokia-bold h-auto bg-secondary-6 overflow-auto bg-opacity-80  w-full z-10 top-0 left-0 bottom-0 transform -translate-x-100 transition-transform ease-in-out duration-200 pr-8 space-y-2 fixed cursor-pointer"
                      : "hidden  cursor-pointer  md:flex md:items-center md:justify-end  md:text-xs lg:text-sm xl:text-xl gap-[0.4rem] lg:space-x-2  xl:space-x-2  text-secondary-6 transition-all"
                  }`}
                >
                  <li>
                    <NavLink
                      to="/"
                      onClick={closeMenu}
                      className={({ isActive }: { isActive: boolean }) =>
                        `hover:text-accent-6 tracking-wide transition-all ${
                          isActive && "text-accent-6 font-nokia-bold"
                        }`
                      }
                    >
                      Home
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/courses"
                      onClick={closeMenu}
                      className={({ isActive }: { isActive: boolean }) =>
                        `hover:text-accent-6 tracking-wide transition-all ${
                          isActive && "text-accent-6 font-nokia-bold"
                        }`
                      }
                      data-testid="courses-link"
                    >
                      Courses
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/sabbathSchool"
                      onClick={closeMenu}
                      className={({ isActive }: { isActive: boolean }) =>
                        `hover:text-accent-6 tracking-wide transition-all ${
                          isActive && "text-accent-6 font-nokia-bold"
                        }`
                      }
                      data-testid="sabbath-school-link"
                    >
                      Sabbath School
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/devotion"
                      onClick={closeMenu}
                      className={({ isActive }: { isActive: boolean }) =>
                        `hover:text-accent-6 tracking-wide transition-all ${
                          isActive && "text-accent-6 font-nokia-bold"
                        }`
                      }
                      data-testid="devotion-link"
                    >
                      Devotion
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/bible"
                      onClick={closeMenu}
                      className={({ isActive }: { isActive: boolean }) =>
                        `hover:text-accent-6 tracking-wide transition-all ${
                          isActive && "text-accent-6 font-nokia-bold"
                        }`
                      }
                    >
                      Bible
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/sermons"
                      onClick={closeMenu}
                      className={({ isActive }: { isActive: boolean }) =>
                        `hover:text-accent-6 tracking-wide transition-all ${
                          isActive && "text-accent-6 font-nokia-bold"
                        }`
                      }
                    >
                      Sermons
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/aboutUs"
                      onClick={closeMenu}
                      className={({ isActive }: { isActive: boolean }) =>
                        `hover:text-accent-6 tracking-wide transition-all ${
                          isActive && "text-accent-6 font-nokia-bold"
                        }`
                      }
                    >
                      About Us
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/contactUs"
                      onClick={closeMenu}
                      className={({ isActive }: { isActive: boolean }) =>
                        `hover:text-accent-6 tracking-wide transition-all ${
                          isActive && "text-accent-6 font-nokia-bold"
                        }`
                      }
                    >
                      Contact Us
                    </NavLink>
                  </li>
                  {user ? (
                    <li ref={ref} className="relative">
                      <div
                        className="flex items-center space-x-2 bg-accent-6 rounded-full py-1 px-2 lg:px-3 xl:px-4 xl:py-2 hover:bg-accent-7 cursor-pointer"
                        onClick={handleAccountClick}
                      >
                        <UserCircle size={24} className="text-primary-1" />
                        <div className="text-xs  xl:text-lg font-medium text-primary-1 tracking-wide">
                          {user.firstName}
                        </div>
                      </div>
                      {showAccountModal && <AccountDropdown />}
                    </li>
                  ) : (
                    <li className="hover:text-gray-400 text-base">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAuthModal(true);
                          closeMenu();
                        }}
                        className="flex items-center gap-1 bg-accent-6 tracking-wide rounded-full w-auto px-2 py-1 lg:px-3 lg:text-sm xl:py-2 xl:px-4 text-xs xl:text-xl hover:bg-accent-7 cursor-pointer transition-all text-primary-1"
                      >
                        <UserCircle size={20} weight="fill" />
                        Account
                      </button>
                    </li>
                  )}
                  <li>
                    <ThemeToggle compact />
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      {/* Header when the scroll is below 20  */}
      <div className="relative z-30 py-2">
        <div className="absolute top-[0.4rem] md:top-0  lg:top-1 xl:top-0   w-full ">
          <div className=" flex justify-between items-center text-white font-nokia-bold w-[90%] md:w-[90%] lg:w-[85%] mx-auto">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
              className="flex justify-center items-center gap-2 md:space-x-0   xl:space-x-1 cursor-pointer "
            >
              <img
                src={LogoImage}
                className="w-4  md:w-5  lg:w-6  xl:w-7 "
                alt=""
              />
              <NavLink to="/" onClick={closeMenu}>
                <h3 className="text-xs md:text-sm lg:text-lg xl:text-2xl tracking-wide">
                  <strong>Ezra</strong> Seminary
                </h3>
              </NavLink>
            </motion.div>
            {/* Navigation and Logo */}
            <nav>
              {/* MenuBar */}
              <div className="md:hidden flex">
                <button
                  onClick={handleMenuClick}
                  className="text-white focus:outline-none "
                >
                  {showMenu ? (
                    <XCircle
                      size={20}
                      className="z-20 fixed top-[2.2rem] left-[90%]"
                    />
                  ) : (
                    <List
                      size={20}
                      className=" z-20 top-[2.7rem] left-[90%] block"
                    />
                  )}
                </button>
              </div>
              {/* Navigation */}
              <ul
                className={`${
                  showMenu
                    ? "flex flex-col justify-center items-end text-xl font-nokia-bold h-auto bg-secondary-6 overflow-auto bg-opacity-80  w-full z-10 top-0 left-0 bottom-0 transform -translate-x-100 transition-transform ease-in-out duration-200 pr-8 space-y-2 fixed cursor-pointer"
                    : "hidden font-Lato-Regular  cursor-pointer  md:flex md:items-center md:justify-end md:text-xs lg:text-sm xl:text-xl gap-[0.4rem] lg:space-x-2  xl:space-x-2  "
                }`}
              >
                <motion.li
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
                >
                  <NavLink
                    to="/"
                    onClick={closeMenu}
                    className={({ isActive }: { isActive: boolean }) =>
                      `hover:text-accent-6 tracking-wide transition-all ${
                        isActive && "text-accent-6 font-nokia-bold"
                      }`
                    }
                  >
                    Home
                  </NavLink>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.4 }}
                >
                  <NavLink
                    to="/courses"
                    onClick={closeMenu}
                    className={({ isActive }: { isActive: boolean }) =>
                      `hover:text-accent-6 tracking-wide transition-all ${
                        isActive && "text-accent-6 font-nokia-bold"
                      }`
                    }
                    data-testid="courses-link"
                  >
                    Courses
                  </NavLink>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.5 }}
                >
                  <NavLink
                    to="/sabbathSchool"
                    onClick={closeMenu}
                    className={({ isActive }: { isActive: boolean }) =>
                      `hover:text-accent-6 tracking-wide transition-all ${
                        isActive && "text-accent-6 font-nokia-bold"
                      }`
                    }
                    data-testid="sabbath-school-link"
                  >
                    Sabbath School
                  </NavLink>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.6 }}
                >
                  <NavLink
                    to="/devotion"
                    onClick={closeMenu}
                    className={({ isActive }: { isActive: boolean }) =>
                      `hover:text-accent-6 tracking-wide transition-all ${
                        isActive && "text-accent-6 font-nokia-bold"
                      }`
                    }
                    data-testid="devotion-link"
                  >
                  Devotion
                </NavLink>
              </motion.li>
                <motion.li
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.68 }}
                >
                  <NavLink
                    to="/bible"
                    onClick={closeMenu}
                    className={({ isActive }: { isActive: boolean }) =>
                      `hover:text-accent-6 tracking-wide transition-all ${
                        isActive && "text-accent-6 font-nokia-bold"
                      }`
                    }
                  >
                    Bible
                  </NavLink>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.7 }}
                >
                  <NavLink
                    to="/sermons"
                    onClick={closeMenu}
                    className={({ isActive }: { isActive: boolean }) =>
                      `hover:text-accent-6 tracking-wide transition-all ${
                        isActive && "text-accent-6 font-nokia-bold"
                      }`
                    }
                  >
                    Sermons
                  </NavLink>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.75 }}
                >
                  <NavLink
                    to="/aboutUs"
                    onClick={closeMenu}
                    className={({ isActive }: { isActive: boolean }) =>
                      `hover:text-accent-6 tracking-wide transition-all ${
                        isActive && "text-accent-6 font-nokia-bold"
                      }`
                    }
                  >
                    About Us
                  </NavLink>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.85 }}
                >
                  <NavLink
                    to="/contactUs"
                    onClick={closeMenu}
                    className={({ isActive }: { isActive: boolean }) =>
                      `hover:text-accent-6 tracking-wide transition-all ${
                        isActive && "text-accent-6 font-nokia-bold"
                      }`
                    }
                  >
                    Contact Us
                  </NavLink>
                </motion.li>
                {user ? (
                  <motion.li
                    initial={{ opacity: 0, y: 100 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: "easeOut", delay: 0.9 }}
                    ref={ref}
                    className="relative"
                  >
                    <div
                      className="flex items-center space-x-2 bg-accent-6 rounded-full py-1 px-2 text-xs1  hover:bg-accent-7 cursor-pointer"
                      onClick={handleAccountClick}
                    >
                      <UserCircle size={24} />
                      <div className="text-xs  xl:text-lg font-medium text-white tracking-wide">
                        {user.firstName}
                      </div>
                    </div>
                    {showAccountModal && <AccountDropdown />}
                  </motion.li>
                ) : (
                  <motion.li
                    initial={{ opacity: 0, y: 100 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: "easeOut", delay: 0.9 }}
                    className="hover:text-primary-6 text-base"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setShowAuthModal(true);
                        closeMenu();
                      }}
                      className="flex items-center gap-1 bg-accent-6 tracking-wide rounded-full font-nokia-bold w-auto px-2 py-1 lg:px-3 lg:text-sm xl:py-2 xl:px-4 text-xs xl:text-xl hover:bg-accent-7 cursor-pointer transition-all"
                    >
                      <UserCircle size={20} weight="fill" />
                      Account
                    </button>
                  </motion.li>
                )}
                <motion.li
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut", delay: 1 }}
                >
                  <ThemeToggle />
                </motion.li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
      <AccountAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </header>
  );
};

export default Header;
